import Vue from 'vue'
import VueRouter from 'vue-router'
import Login from '@/views/authentication/Login'
import Chat from '@/views/Chat'
import ForgotPassword from '@/views/authentication/ForgotPassword'
import Places from '@/views/places/Places'
import Place from '@/views/places/Place'
import Sessions from '@/views/Sessions'
import EditFleet from '@/views/fleets/EditFleet'
import Home from '@/views/Home'
import Reports from '@/views/reports/Reports'
import Users from '@/views/Users'
import Profile from '@/views/Profile'
import GateControllers from '@/views/GateControllers'
import MonthlyPasses from '@/views/MonthlyPasses'
import Campaigns from '@/views/Campaigns'
import Campaign from '@/views/Campaign'
import Enforcement from '@/views/Enforcement'
import store from '@/store'
import Settings from '@/views/settings/Settings'
import OperatorSettings from '@/views/settings/OperatorSettings'
import Zone from '@/views/EditZone'
import ParkingFineDetailView from '@/views/ParkingFineDetailView'
import PasswordReset from '@/views/PasswordReset'
import jwtDecode from 'jwt-decode'
import OwnerActivation from '@/views/OwnerActivation'
import Booking from '@/views/Booking'
import VehicleGroups from '@/views/VehicleGroups'
import CarWashDisputes from '@/views/CarWashDisputes'
import Disputes from '@/views/Disputes'
import EnforcementOfficerActivation from '@/views/EnforcementOfficerActivation'
import { legacyHomeLandingForUser } from '@/utils/landing'

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    name: 'root',
    component: Home,
    meta: {
      requiresAuth: true
    },
    beforeEnter: (to, from, next) => {
      const target = legacyHomeLandingForUser(
        store.getters['authentication/user']
      )
      if (target.external) {
        window.location.replace(target.path)
        next(false)
        return
      }
      next(target.path)
    }
  },
  {
    path: '/chat',
    name: 'chat',
    component: Chat,
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      requiresAuth: false
    },
    // Login now lives in the v3 app — it's the single login for everyone. Same origin in both prod
    // (Firebase rewrites /v3/**) and dev (the v2 dev server proxies /v3 to the v3 dev server), so a
    // relative redirect keeps the session intact.
    beforeEnter: () => {
      window.location.href = '/v3/login'
    }
  },
  {
    path: '/forgot-pwd',
    name: 'forgot-pwd',
    component: ForgotPassword,
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/places',
    name: 'places',
    component: Places,
    meta: {
      requiresAuth: true,
      placeAdminCanSee: true
    }
  },
  {
    path: '/place/:id',
    name: 'place',
    component: Place,
    props: true,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/sessions',
    name: 'sessions',
    component: Sessions,
    meta: {
      requiresAuth: true,
      placeAdminOrBookingsAdminCanSee: true
    }
  },
  {
    path: '/users',
    name: 'users',
    component: Users,
    meta: {
      requiresAuth: true,
      placeAdminCanSee: true
    }
  },
  {
    path: '/fleet/:id',
    name: 'fleet',
    component: EditFleet,
    props: true,
    meta: {
      requiresAuth: true,
      adminOnly: false,
      globaAdminOnly: false
    }
  },
  {
    path: '/fleets',
    name: 'fleets',
    component: Home,
    meta: {
      requiresAuth: true
    },
    beforeEnter: (to, from, next) => {
      const { roles } = store.getters['authentication/user']
      if (
        roles.includes('BOOKINGS_ADMIN') ||
        roles.includes('BOOKINGS_ADMIN_NO_SESSIONS')
      ) {
        next('/booking')
      } else {
        next()
      }
    }
  },
  {
    path: '/home',
    name: 'home-legacy',
    component: Home,
    meta: {
      requiresAuth: true
    },
    beforeEnter: (to, from, next) => {
      const target = legacyHomeLandingForUser(
        store.getters['authentication/user']
      )
      if (target.external) {
        window.location.replace(target.path)
        next(false)
        return
      }
      next(target.path)
    }
  },
  {
    path: '/report',
    name: 'report',
    component: Reports,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/profile',
    name: 'profile',
    component: Profile,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/gate-controllers/:id',
    name: 'gate-controllers',
    component: GateControllers,
    props: true,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/monthly-passes/:id',
    name: 'monthly-passes',
    component: MonthlyPasses,
    props: true,
    meta: {
      requiresAuth: true,
      placeAdminCanSee: true
    }
  },
  {
    path: '/campaigns',
    name: 'campaigns',
    component: Campaigns,
    meta: {
      requiresAuth: true,
      placeAdminCanSee: true
    }
  },
  {
    path: '/campaign/:id',
    name: 'campaign',
    component: Campaign,
    props: true,
    meta: {
      requiresAuth: true,
      placeAdminCanSee: true
    }
  },
  {
    path: '/enforcement',
    name: 'enforcement',
    component: Enforcement,
    props: true,
    meta: {
      requiresAuth: true,
      requiresToBeEnforcer: true
    }
  },
  {
    path: '/settings',
    name: 'settings',
    component: Settings,
    meta: {
      requiresAuth: true,
      adminOnly: true,
      globaAdminOnly: true
    }
  },
  {
    path: '/settings/operator',
    name: 'operator settings',
    component: OperatorSettings,
    meta: {
      requiresAuth: true,
      adminOnly: true,
      globaAdminOnly: true
    }
  },
  {
    path: '/zone/:id',
    name: 'zone',
    component: Zone,
    props: true,
    meta: {
      requiresAuth: true,
      adminOnly: true,
      globaAdminOnly: true
    }
  },
  {
    path: '/parking-fine/:id',
    name: 'parking-fine',
    component: ParkingFineDetailView,
    props: true,
    meta: {
      requiresAuth: true,
      adminOnly: true,
      globaAdminOnly: true
    }
  },
  {
    path: '/reset/:uuid',
    name: 'reset',
    component: PasswordReset,
    props: true,
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/owner/activate/:email/:uuid',
    name: 'owner-activate',
    component: OwnerActivation,
    props: true,
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/enforcement-officer/activate/:email/:uuid',
    name: 'enforcement-officer-activate',
    component: EnforcementOfficerActivation,
    props: true,
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/booking',
    name: 'booking',
    component: Booking,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/vehicle-groups',
    name: 'vehicle-groups',
    component: VehicleGroups
  },
  {
    path: '/place/:placeId/disputes',
    name: 'carWashDisputes',
    component: CarWashDisputes,
    props: true,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/disputes',
    name: 'disputes',
    component: Disputes,
    props: true,
    meta: {
      requiresAuth: true
    }
  }
]

const router = new VueRouter({
  mode: 'history',
  routes
})

router.beforeEach((to, from, next) => {
  // if (from.path === '/chat') {
  //   next('/chat')
  // }
  // if (router.currentRoute.name !== '/chat') {
  //   setTimeout(() => {
  //     window.Tawk_API.hideWidget()
  //   }, 1000)
  // }
  if (from.path.includes('/place/')) {
    store.commit('places/setPlace', undefined)
  }

  if (from.name === 'parking-fine') {
    store.commit('enforcement/setSelectedParkingFine', undefined)
  }
  if (store.state.app.error) {
    store.commit('app/setError', null)
  }
  const user = JSON.parse(localStorage.getItem('user'))
  const jwt = localStorage.getItem('jwt')

  // Check if route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    // If JWT exists but is actually expired we can already log the user out without waiting/relying on next request to the userlogout.
    if (jwt) {
      const decodedJWT = jwtDecode(jwt)
      if (Date.now() >= decodedJWT.exp * 1000) {
        store.dispatch('authentication/logout')
        next({
          path: '/login',
          params: { nextUrl: to.fullPath }
        })
        return
      }
    }
    // Check if user is logged in and direct to login if not authenticated
    if (!jwt || jwt === '' || !user) {
      next({
        path: '/login',
        params: { nextUrl: to.fullPath }
      })
    } else {
      // If authenticated set user from localStorage "autologin"
      store.commit('authentication/setJwt', user.token)
      store.commit('authentication/setUser', user)
      store.commit('authentication/setIsAuthenticated', true)
      const currentUser = store.getters['authentication/user']

      // A Jūrmala-only user (has CAN_SEE_JURMALA_DAY_PASSES but is not an admin) has nothing in the
      // v2 app — send them to the v3 day-pass app. /profile stays reachable so they can still manage
      // their account and log out.
      if (
        currentUser.canSeeJurmalaDayPasses &&
        !currentUser.isAdmin &&
        to.path !== '/profile'
      ) {
        window.location.replace('/v3/jurmala')
        return
      }

      // TODO:: make every route into if block like this. Currently not to break someone's flow, we keep the if sphagetti below also
      if (to.name === 'places') {
        if (
          currentUser.isAdmin ||
          currentUser.isPlaceAdmin ||
          currentUser.canEditAvailabilityTable ||
          currentUser.hasOperatorRole
        ) {
          next()
        } else {
          next('/')
        }
        return
      }

      if (to.matched.some(record => record.meta.adminOnly)) {
        if (currentUser.isAdmin) {
          next()
        } else {
          next('/')
        }
      } else if (to.matched.some(record => record.meta.requiresToBeEnforcer)) {
        if (
          currentUser.isAdmin ||
          currentUser.canViewParkingFines ||
          currentUser.canCancelParkingFines
        ) {
          next()
        } else {
          next('/')
        }
      } else if (to.matched.some(record => record.meta.placeAdminCanSee)) {
        if (
          currentUser.isPlaceAdmin ||
          currentUser.isAdmin ||
          currentUser.canEditAvailabilityTable
        ) {
          next()
        } else {
          next('/')
        }
      } else if (
        to.matched.some(record => record.meta.placeAdminOrBookingsAdminCanSee)
      ) {
        if (
          currentUser.isPlaceAdmin ||
          currentUser.isAdmin ||
          currentUser.canEditAvailabilityTable ||
          currentUser.roles.includes('BOOKINGS_ADMIN')
        ) {
          next()
        } else {
          next('/')
        }
      } else if (to.matched.some(record => record.meta.globaAdminOnly)) {
        if (store.getters['authentication/isGlobalAdmin']) {
          next()
        } else {
          next('/')
        }
      } else if (
        to.matched.some(
          record => !record.meta.globaAdminOnly && !record.meta.adminOnly
        )
      ) {
        next()
      }
      // next()
    }
  } else if (to.matched.some(record => !record.meta.requiresAuth)) {
    // If authenticated do not show login page
    if ((to.path === '/login' || to.path === '/register') && user && jwt) {
      next('/')
    } else {
      next()
    }
  }
})

export default router
